import { type ChangeEvent, type KeyboardEvent, memo, type Ref, useCallback } from 'react';

import { getWorkspaceAgentSelectAssetActiveOptionId } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/getWorkspaceAgentSelectAssetOptionId';
import { useWorkspaceAgentSelectAssetSearchKeyDown } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/useWorkspaceAgentSelectAssetSearchKeyDown';
import { WorkspaceAgentSelectAssetSearchInput } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/WorkspaceAgentSelectAssetSearchInput';
import {
  useWorkspaceAgentSelectAssetStore,
  type WorkspaceAgentSelectAssetStoreState,
} from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/WorkspaceAgentSelectAssetStoreContext';

const getAssetHighlightedIndex = (state: WorkspaceAgentSelectAssetStoreState) => state.assetHighlightedIndex;

const getAssetResults = (state: WorkspaceAgentSelectAssetStoreState) => state.assetResults;

const handleAssetSearchEscape = (event: KeyboardEvent<HTMLElement>, state: WorkspaceAgentSelectAssetStoreState) => {
  if (state.isBoardPickerOpen) {
    event.preventDefault();
    state.setBoardPickerOpen(false);
    return;
  }

  state.setOpen(false);
};

export type WorkspaceAgentSelectAssetSearchFieldProps = {
  inputRef?: Ref<HTMLInputElement>;
};

export const WorkspaceAgentSelectAssetSearchField = memo(({ inputRef }: WorkspaceAgentSelectAssetSearchFieldProps) => {
  const query = useWorkspaceAgentSelectAssetStore((state) => state.query);
  const setQuery = useWorkspaceAgentSelectAssetStore((state) => state.setQuery);
  const moveHighlight = useWorkspaceAgentSelectAssetStore((state) => state.moveAssetHighlight);
  const placeholder = useWorkspaceAgentSelectAssetStore((state) =>
    state.source === 'board' && state.selectedBoard?.title ? `Search ${state.selectedBoard.title}` : 'Search',
  );
  const activeOptionId = useWorkspaceAgentSelectAssetStore((state) =>
    getWorkspaceAgentSelectAssetActiveOptionId(state.assetResults.items, state.assetHighlightedIndex),
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setQuery(event.target.value);
    },
    [setQuery],
  );

  const handleClear = useCallback(() => {
    setQuery('');
  }, [setQuery]);

  const { handleKeyDown } = useWorkspaceAgentSelectAssetSearchKeyDown({
    getHighlightedIndex: getAssetHighlightedIndex,
    getResults: getAssetResults,
    moveHighlight,
    onEscape: handleAssetSearchEscape,
  });

  return (
    <WorkspaceAgentSelectAssetSearchInput
      activeOptionId={activeOptionId}
      inputRef={inputRef}
      onChange={handleChange}
      onClear={handleClear}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      query={query}
    />
  );
});

WorkspaceAgentSelectAssetSearchField.displayName = 'WorkspaceAgentSelectAssetSearchField';
